import React, { useState } from 'react';
import { 
  Upload, 
  Sparkles, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  UserPlus, 
  Calendar, 
  Tag, 
  ArrowRight,
  Loader2,
  X,
  Image as ImageIcon
} from 'lucide-react';
import { FamilyMember, Story, ArchiveItem } from '../types';

interface UploadModalProps {
  allMembers: FamilyMember[];
  onAddStory: (story: Story) => void;
  onAddArchiveItem: (item: ArchiveItem) => void;
  onClose: () => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  allMembers,
  onAddStory,
  onAddArchiveItem,
  onClose
}) => {
  const [step, setStep] = useState<number>(1);
  const [uploadType, setUploadType] = useState<'photo' | 'document' | 'story'>('photo');

  // File & Preview State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewBase64, setPreviewBase64] = useState<string>('');

  // AI Loading & Results
  const [isAiProcessing, setIsAiProcessing] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string>('');

  // Form Metadata State
  const [title, setTitle] = useState('');
  const [year, setYear] = useState<number>(1950);
  const [decade, setDecade] = useState('1950s');
  const [description, setDescription] = useState('');
  const [transcription, setTranscription] = useState('');
  const [addedBy, setAddedBy] = useState('');
  const [taggedMemberIds, setTaggedMemberIds] = useState<string[]>([]);
  const [tagsString, setTagsString] = useState('Family, Heirloom');

  // Quick Story Notes for AI Story Generator
  const [quickStoryNotes, setQuickStoryNotes] = useState('');

  // Handle File Selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewBase64(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Run AI Analysis via Express server endpoints
  const handleRunAiAssistant = async () => {
    if (!previewBase64 && uploadType !== 'story') {
      alert('Please select an image or document scan first.');
      return;
    }

    setIsAiProcessing(true);
    setAiError('');

    try {
      if (uploadType === 'photo') {
        const res = await fetch('/api/gemini/analyze-photo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: previewBase64,
            mimeType: selectedFile?.type || 'image/jpeg'
          })
        });

        const data = await res.json();
        if (data.success && data.data) {
          setTitle(data.data.title || '');
          setDescription(data.data.description || '');
          if (data.data.estimatedDecade) setDecade(data.data.estimatedDecade);
          if (data.data.tags && Array.isArray(data.data.tags)) {
            setTagsString(data.data.tags.join(', '));
          }
          if (data.data.detectedText) {
            setTranscription(data.data.detectedText);
          }
        } else {
          setAiError(data.error || 'Could not analyze photo.');
        }
      } else if (uploadType === 'document') {
        const res = await fetch('/api/gemini/transcribe-document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: previewBase64,
            mimeType: selectedFile?.type || 'image/jpeg'
          })
        });

        const data = await res.json();
        if (data.success && data.data) {
          setTranscription(data.data.transcription || '');
          setDescription(data.data.summary || '');
          setTitle(data.data.summary ? `Letter/Document: ${data.data.summary.slice(0, 40)}...` : 'Historical Document Transcribed');
        } else {
          setAiError(data.error || 'Could not transcribe document.');
        }
      } else if (uploadType === 'story') {
        const primaryMember = allMembers.find(m => taggedMemberIds.includes(m.id));
        const res = await fetch('/api/gemini/narrate-story', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quickNotes: quickStoryNotes,
            relativeName: primaryMember ? `${primaryMember.firstName} ${primaryMember.lastName}` : 'Ancestor',
            year: year.toString()
          })
        });

        const data = await res.json();
        if (data.success && data.data) {
          setTitle(data.data.title || 'Family Story');
          setDescription(data.data.storyContent || '');
          if (data.data.suggestedTags) setTagsString(data.data.suggestedTags.join(', '));
        } else {
          setAiError(data.error || 'Could not generate story narration.');
        }
      }

      setStep(3); // Advance to final verification step
    } catch (err: any) {
      console.error('AI Error:', err);
      setAiError('Network error or server API key issue. Proceeding with manual metadata.');
      setStep(3);
    } finally {
      setIsAiProcessing(false);
    }
  };

  const toggleTagMember = (id: string) => {
    setTaggedMemberIds(prev => 
      prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
    );
  };

  const handleFinalSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      alert('Please enter a title for this item.');
      return;
    }

    const tagsArray = tagsString.split(',').map(t => t.trim()).filter(Boolean);

    if (uploadType === 'story') {
      const newStory: Story = {
        id: 's_' + Date.now(),
        title: title.trim(),
        year: year,
        decade: decade,
        authorName: addedBy.trim() || 'Family Historian',
        category: 'oral_history',
        summary: description.slice(0, 150) + '...',
        content: description,
        coverImageUrl: previewBase64 || 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&q=80&w=600',
        taggedMemberIds: taggedMemberIds,
        tags: tagsArray,
        dateAdded: new Date().toISOString().split('T')[0],
        comments: [],
        likesCount: 1
      };
      onAddStory(newStory);
    } else {
      const newItem: ArchiveItem = {
        id: 'a_' + Date.now(),
        title: title.trim(),
        type: uploadType === 'document' ? 'document' : 'photo',
        fileUrl: previewBase64 || 'https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&q=80&w=600',
        year: year,
        decade: decade,
        description: description,
        transcribedText: transcription,
        taggedMemberIds: taggedMemberIds,
        tags: tagsArray,
        dateAdded: new Date().toISOString().split('T')[0],
        addedBy: addedBy.trim() || 'Family Member'
      };
      onAddArchiveItem(newItem);
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-3 md:p-6 overflow-y-auto">
      <div className="bg-[#FAF8F5] border-4 border-[#2C221E] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative text-[#2C221E]">
        
        {/* Header */}
        <div className="bg-[#2C221E] text-white p-3.5 sm:p-5 md:p-6 sticky top-0 z-10 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <span className="bg-amber-500 text-black text-[11px] sm:text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">
              Step {step} of 3
            </span>
            <h3 className="font-serif text-lg sm:text-2xl font-bold text-white mt-1 truncate">
              Add New Historical Memory
            </h3>
          </div>

          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] p-2 bg-white/20 hover:bg-red-600 text-white rounded-xl transition flex items-center justify-center cursor-pointer shrink-0"
            aria-label="Close upload modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Wizard Steps Content */}
        <div className="p-4 sm:p-6 md:p-8 space-y-6">
          
          {/* STEP 1: Select Type & Upload File */}
          {step === 1 && (
            <div className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-[#2C221E] mb-2">
                  What type of memory are you preserving today?
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setUploadType('photo')}
                    className={`min-h-[64px] p-3.5 sm:p-4 rounded-xl border-2 text-left transition cursor-pointer ${
                      uploadType === 'photo'
                        ? 'border-[#8B263E] bg-[#8B263E]/10 font-bold'
                        : 'border-[#D8CFC4] bg-white hover:border-[#8B263E]'
                    }`}
                  >
                    <ImageIcon className="w-5 sm:w-6 h-5 sm:h-6 text-[#8B263E] mb-1" />
                    <p className="text-xs sm:text-sm">Historic Photo</p>
                    <p className="text-[11px] sm:text-xs text-[#6E5E55]">Old family portraits, reunions</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setUploadType('document')}
                    className={`min-h-[64px] p-3.5 sm:p-4 rounded-xl border-2 text-left transition cursor-pointer ${
                      uploadType === 'document'
                        ? 'border-[#8B263E] bg-[#8B263E]/10 font-bold'
                        : 'border-[#D8CFC4] bg-white hover:border-[#8B263E]'
                    }`}
                  >
                    <FileText className="w-5 sm:w-6 h-5 sm:h-6 text-[#8B263E] mb-1" />
                    <p className="text-xs sm:text-sm">Letter or Scan</p>
                    <p className="text-[11px] sm:text-xs text-[#6E5E55]">Handwritten letters, deeds</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setUploadType('story')}
                    className={`min-h-[64px] p-3.5 sm:p-4 rounded-xl border-2 text-left transition cursor-pointer ${
                      uploadType === 'story'
                        ? 'border-[#8B263E] bg-[#8B263E]/10 font-bold'
                        : 'border-[#D8CFC4] bg-white hover:border-[#8B263E]'
                    }`}
                  >
                    <Sparkles className="w-5 sm:w-6 h-5 sm:h-6 text-[#8B263E] mb-1" />
                    <p className="text-xs sm:text-sm">Oral Story</p>
                    <p className="text-[11px] sm:text-xs text-[#6E5E55]">Written or spoken notes</p>
                  </button>
                </div>
              </div>

              {/* Upload Input for Photo / Document */}
              {uploadType !== 'story' ? (
                <div className="border-4 border-dashed border-[#D8CFC4] hover:border-[#8B263E] rounded-2xl p-5 sm:p-8 text-center bg-white space-y-3 cursor-pointer">
                  <Upload className="w-10 sm:w-12 h-10 sm:h-12 text-[#8B263E] mx-auto" />
                  <div>
                    <p className="font-bold text-sm sm:text-base text-[#2C221E]">
                      Tap or Drag your photo / document here
                    </p>
                    <p className="text-xs text-[#6E5E55] mt-1">
                      Supports JPG, PNG, WEBP (Max 20MB)
                    </p>
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="block w-full text-xs sm:text-sm text-[#6E5E55] file:mr-3 sm:file:mr-4 file:py-2.5 file:px-3 sm:file:px-4 file:rounded-xl file:border-0 file:text-xs sm:file:text-sm file:font-bold file:bg-[#8B263E] file:text-white hover:file:bg-[#6B1D2F] cursor-pointer"
                  />

                  {previewBase64 && (
                    <div className="mt-3 p-2 border border-[#D8CFC4] rounded-xl bg-[#F2ECE1]">
                      <p className="text-xs font-bold text-emerald-800 mb-2">✓ Photo Loaded Preview:</p>
                      <img src={previewBase64} alt="Preview" className="h-32 sm:h-40 mx-auto object-contain rounded-lg" />
                    </div>
                  )}
                </div>
              ) : (
                /* Story Notes Input */
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-bold text-[#2C221E]">
                    Enter quick bullet notes or rough story outline:
                  </label>
                  <textarea
                    rows={5}
                    value={quickStoryNotes}
                    onChange={e => setQuickStoryNotes(e.target.value)}
                    placeholder="e.g. Grandpa Arthur built his first carpentry shop on Tremont Street in 1925..."
                    className="w-full p-3 bg-white rounded-xl border-2 border-[#D8CFC4] text-xs sm:text-sm text-[#2C221E]"
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!previewBase64 && !quickStoryNotes}
                className="min-h-[48px] w-full py-3 sm:py-3.5 bg-[#8B263E] disabled:bg-gray-400 text-white font-bold text-sm sm:text-base rounded-xl hover:bg-[#6B1D2F] transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
              >
                <span>Continue to AI Assistant Step</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* STEP 2: Run AI Archival Assistant */}
          {step === 2 && (
            <div className="space-y-6 text-center py-4">
              <div className="w-16 h-16 bg-[#8B263E]/10 rounded-full flex items-center justify-center mx-auto text-[#8B263E]">
                <Sparkles className="w-8 h-8 animate-spin" />
              </div>

              <div>
                <h4 className="font-serif text-2xl font-bold text-[#2C221E]">
                  Let AI Scan & Catalog Your Historical Record
                </h4>
                <p className="text-sm text-[#6E5E55] mt-1 max-w-lg mx-auto">
                  Our Gemini Archival Assistant will transcribe handwritten text, estimate the decade, suggest a warm title, and recommend tags.
                </p>
              </div>

              {isAiProcessing ? (
                <div className="p-6 bg-amber-50 rounded-xl border-2 border-amber-300 text-amber-900 flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-700" />
                  <span className="font-bold text-sm">Scanning image & transcribing historical text...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleRunAiAssistant}
                    className="w-full py-4 bg-[#8B263E] text-white font-bold text-lg rounded-xl hover:bg-[#6B1D2F] transition shadow-md flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5 text-amber-300" />
                    <span>Run AI Archival Scan Now</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="text-xs text-[#6E5E55] underline hover:text-[#2C221E]"
                  >
                    Skip AI scan and enter details manually →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Review & Tag Family Members */}
          {step === 3 && (
            <form onSubmit={handleFinalSave} className="space-y-5">
              
              {aiError && (
                <div className="p-3 bg-amber-100 border border-amber-400 rounded-lg text-xs text-amber-900 font-medium">
                  Notice: {aiError} You can review and edit details below.
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                    Title / Caption
                  </label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Grandpa Arthur on the Front Porch (~1940s)"
                    className="w-full p-3 bg-white rounded-xl border-2 border-[#D8CFC4] text-sm text-[#2C221E] font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                    Estimated Year
                  </label>
                  <input
                    type="number"
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                    className="w-full p-2.5 bg-white rounded-xl border-2 border-[#D8CFC4] text-sm text-[#2C221E]"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                    Decade Era
                  </label>
                  <select
                    value={decade}
                    onChange={e => setDecade(e.target.value)}
                    className="w-full p-2.5 bg-white rounded-xl border-2 border-[#D8CFC4] text-sm text-[#2C221E]"
                  >
                    <option value="1900s">1900s</option>
                    <option value="1910s">1910s</option>
                    <option value="1920s">1920s</option>
                    <option value="1930s">1930s</option>
                    <option value="1940s">1940s</option>
                    <option value="1950s">1950s+</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                  Description / Story Text
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full p-3 bg-white rounded-xl border-2 border-[#D8CFC4] text-sm text-[#2C221E]"
                />
              </div>

              {/* Transcription if document */}
              {uploadType === 'document' && (
                <div>
                  <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                    Verbatim Text Transcription
                  </label>
                  <textarea
                    rows={3}
                    value={transcription}
                    onChange={e => setTranscription(e.target.value)}
                    placeholder="Handwritten letter text..."
                    className="w-full p-3 bg-white rounded-xl border-2 border-[#D8CFC4] text-xs font-mono text-[#2C221E]"
                  />
                </div>
              )}

              {/* Tag Family Members */}
              <div className="bg-[#F2ECE1] p-4 rounded-xl border border-[#D8CFC4] space-y-2">
                <label className="block text-xs uppercase font-bold text-[#8B263E]">
                  Tag Relatives Appearing in this Memory:
                </label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pt-1">
                  {allMembers.map(m => {
                    const isTagged = taggedMemberIds.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleTagMember(m.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                          isTagged
                            ? 'bg-[#8B263E] text-white border-[#8B263E]'
                            : 'bg-white text-[#2C221E] border-[#D8CFC4] hover:bg-[#D8CFC4]'
                        }`}
                      >
                        {isTagged ? '✓ ' : '+ '} {m.firstName} {m.lastName}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Added By Name */}
              <div>
                <label className="block text-xs uppercase font-bold text-[#8B263E] mb-1">
                  Your Name (Contributor)
                </label>
                <input
                  type="text"
                  value={addedBy}
                  onChange={e => setAddedBy(e.target.value)}
                  placeholder="e.g. Cousin David"
                  className="w-full p-2.5 bg-white rounded-xl border-2 border-[#D8CFC4] text-sm text-[#2C221E]"
                />
              </div>

              <div className="pt-2 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-sm text-[#6E5E55] font-bold hover:underline"
                >
                  ← Back to Step 1
                </button>

                <button
                  type="submit"
                  className="px-6 py-3.5 bg-[#8B263E] text-white font-bold text-base rounded-xl hover:bg-[#6B1D2F] transition shadow-md"
                >
                  Save Memory to Archive Vault
                </button>
              </div>

            </form>
          )}

        </div>

      </div>
    </div>
  );
};
